import * as Blockly from 'blockly/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { missionManager } from '../../blocks/mission'
import { MissionResponseData } from '../../openapi'
import { buildWorkspaces } from '../../preview/buildWorkspaces'
import { loadSceneFromUrl, sceneJsonUrl } from '../../preview/sceneLoader'
import { SceneDescriptor } from '../../preview/types'
import { normalizeStageSlug } from './previewDisplayMode'
import {
  buildSceneFromMissionObjects,
  getSceneForMission,
} from './sceneRegistry'

/**
 * ミッション情報から '{slug}_{num}' 形式のシーンキーを作る（不正値で undefined）。
 * SCENE_MANIFEST には rb1/rb2/rj1/rj2 系のキーしか無いため、rjs/rjf 等の別名は
 * normalizeStageSlug で寄せる（rb 系と rj 系は同じテンプレを指す）。
 */
const buildMissionKey = (
  missionInfo: MissionResponseData | undefined,
): string | undefined => {
  const slug = missionInfo?.slug
  const num = missionInfo?.num
  if (!slug || typeof num !== 'number' || !Number.isFinite(num)) {
    return undefined
  }
  return `${normalizeStageSlug(slug)}_${num}`
}

/** Blockly 編集後にプレビューへ反映するまでのデバウンス時間（ms）。 */
const REFRESH_DEBOUNCE_MS = 300

const filterSerializedWorkspaces = (
  workspaces: Record<string, unknown>,
): Record<string, Record<string, unknown>> => {
  const result: Record<string, Record<string, unknown>> = {}
  for (const [name, data] of Object.entries(workspaces)) {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      result[name] = data as Record<string, unknown>
    }
  }
  return result
}

const disposeWorkspaces = (
  workspaces: Map<string, Blockly.Workspace>,
): void => {
  for (const workspace of workspaces.values()) {
    try {
      workspace.dispose()
    } catch (error) {
      console.error('プレビュー用ワークスペースの破棄に失敗しました', error)
    }
  }
}

const collectMissionObjectNames = (): string[] => {
  return missionManager.objects
    .map((obj) => obj.name)
    .filter(
      (name): name is string => typeof name === 'string' && name.length > 0,
    )
}

export interface PreviewSnapshotState {
  scene: SceneDescriptor
  workspaces: Map<string, Blockly.Workspace> | null
  /** スナップショット識別子。値が変わると PreviewPanel を再マウントする。 */
  refreshKey: number
  /** 手動で最新のシリアライズ状態を取り直す（緊急脱出用）。 */
  refresh: () => void
}

/**
 * おためしプレイの「常時表示・ライブ同期」を担うフック。
 *
 * - 受け取った `missionInfo` と `missionManager.objects` から SceneDescriptor を
 *   動的に決定する：まず静的レジストリ（`rjf01Scene` 等）を引き、無ければ
 *   MissionData のオブジェクト名から自動配置のシーンを生成する。
 *   これによりどのミッションでも Blockly workspace と object name が確実に
 *   一致し、`onPlay` などのハンドラが必ず登録される。
 * - マウント時に最初のスナップショットを作る。
 * - `missionManager.on('workspace')` と `'change'` を購読し、Blockly 編集や
 *   ミッション切替のたびにデバウンスして scene/workspaces を再構築する。
 */
export const usePreviewModal = (
  missionInfo: MissionResponseData | undefined,
): PreviewSnapshotState => {
  // missionKey は文字列なので、missionInfo の参照が変わっても同一ミッションなら同値。
  // EXP 取得などで missionInfo が新規参照になっても、scene 更新トリガを抑止する。
  const missionKey = useMemo(() => buildMissionKey(missionInfo), [missionInfo])

  // 'missionInfo' を非依存で参照するため ref に保持。scene 構築時に最新値を読む。
  const missionInfoRef = useRef(missionInfo)
  useEffect(() => {
    missionInfoRef.current = missionInfo
  }, [missionInfo])

  const computeSyncScene = useCallback(
    (): SceneDescriptor =>
      getSceneForMission(missionInfoRef.current) ??
      buildSceneFromMissionObjects(
        missionInfoRef.current,
        collectMissionObjectNames(),
      ),
    [],
  )

  const [scene, setScene] = useState<SceneDescriptor>(computeSyncScene)
  const [workspaces, setWorkspaces] = useState<Map<
    string,
    Blockly.Workspace
  > | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // プロジェクトが切り替わった（ファイルメニュー > あたらしくつくる / 別プロジェクトを
  // 開いた）ときは、ワークスペース差し替えだけでなくプレビューを完全に初期化する。
  // refreshKey を増やすと PreviewModal 側で PreviewPanel が再 mount され、World・
  // アバター位置・実行状態が新規プロジェクトの初期状態に戻る（過去のプログラムの
  // 実行結果が残らない）。Blockly 編集による refresh（下記）では増やさない。
  // 直近スナップショットのシリアライズ内容。動画チャプター切替など、ワークスペースの
  // 内容が変わらない 'change' イベントでも refresh は走るため、内容が同一なら
  // 差し替え自体をスキップする（差し替えると PreviewPanel 側で「実行中の編集」と
  // みなされ、実行中のプレビューが停止してしまう）。
  const lastSnapshotJsonRef = useRef<string | null>(null)

  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId') ?? ''
  const previousProjectIdRef = useRef(projectId)
  const refreshRef = useRef<() => void>(() => {})
  useEffect(() => {
    if (previousProjectIdRef.current === projectId) {
      return
    }
    previousProjectIdRef.current = projectId
    setRefreshKey((key) => key + 1)
    // プロジェクト切替は「完全初期化」の経路なので、内容が偶然一致しても
    // スナップショット比較でスキップせず必ず再構築する。
    lastSnapshotJsonRef.current = null
    // ワークスペースのスナップショットも即時に作り直す。missionManager の
    // 'change' イベント経由の refresh はデバウンス（300ms）があり、その間に
    // 旧プロジェクトのプログラムが新しいパネルへ渡るのを防ぐ。
    refreshRef.current()
  }, [projectId])

  // unmount 時に確実に dispose するための最新参照。
  const workspacesRef = useRef<Map<string, Blockly.Workspace> | null>(null)
  useEffect(() => {
    workspacesRef.current = workspaces
  }, [workspaces])

  // 破棄を 1 世代遅延するための退避先（直前まで state にあった Map）。
  // 実行中の runtime（PreviewPanel）は state の workspaces をまだ参照しており、
  // その破棄は次コミット後の effect cleanup で走る。即時 dispose すると、クローンの
  // プログラム起動が setTimeout 後に workspace.getTopBlocks() を遅延参照する窓で
  // 「破棄済み Workspace＝top blocks 空」を読み、クローンが出ない/消える。
  const pendingDisposeRef = useRef<Map<string, Blockly.Workspace> | null>(null)

  // workspace のみ差し替える（scene は missionInfo 切替/動的フェッチ完了時にのみ更新）。
  // 'refreshKey' は意図的に増やさない: 増やすと PreviewModal 側で PreviewPanel が
  // 再 mount され、World / アバター位置 / 物理状態がすべて初期化されてしまうため。
  // PreviewPanel 側は workspaces の変化を 'createRuntime' 再生成だけで吸収する。
  const refresh = useCallback((): void => {
    let next: Map<string, Blockly.Workspace> | null
    try {
      const saved = filterSerializedWorkspaces(
        missionManager.saveData.workspaces,
      )
      const json = JSON.stringify(saved)
      if (json === lastSnapshotJsonRef.current) {
        return
      }
      lastSnapshotJsonRef.current = json
      // 復元中だけ選択中オブジェクトを無効化する（理由は
      // missionManager.runWithoutCurrentObject の doc を参照）。
      next = missionManager.runWithoutCurrentObject(() =>
        buildWorkspaces(saved),
      )
    } catch (error) {
      console.error(
        'おためしプレイ用スナップショットの作成に失敗しました',
        error,
      )
      lastSnapshotJsonRef.current = null
      next = null
    }
    setWorkspaces((prev) => {
      // prev は実行中 runtime がまだ参照している可能性があるため即時破棄しない。
      // 1 世代前の退避分（runtime が確実に切り替わった後の Map）だけを破棄する。
      const stale = pendingDisposeRef.current
      pendingDisposeRef.current = prev
      if (stale && stale !== prev) {
        disposeWorkspaces(stale)
      }
      return next
    })
  }, [])
  // projectId 変化エフェクト（refresh より前に定義）から最新の refresh を呼ぶための ref。
  refreshRef.current = refresh

  // ミッションが変わったときだけ scene を更新する。
  // - missionKey（文字列）に依存するので、'missionInfo' の参照だけが変わる
  //   （EXP 取得等）では再実行されず、scene の参照も変わらない。
  // - 動的 JSON フェッチを優先し、失敗時のみ同期フォールバックへ落ちる。
  //   これにより 'sync → async' の二段更新によるリセットも避けられる。
  useEffect(() => {
    if (!missionKey) {
      setScene(computeSyncScene())
      return
    }
    let cancelled = false
    loadSceneFromUrl(sceneJsonUrl(missionKey))
      .then((loaded) => {
        if (cancelled) return
        setScene(loaded)
      })
      .catch((error) => {
        if (cancelled) return
        // 404 等は当該ミッションの動的シーンが無いだけなので info 扱い。
        console.info(
          `動的シーンが無いためフォールバックします (${missionKey}):`,
          error,
        )
        setScene(computeSyncScene())
      })
    return () => {
      cancelled = true
    }
  }, [missionKey, computeSyncScene])

  // 初回スナップショット＋イベント購読（Blockly 編集 / ミッション切替）。
  useEffect(() => {
    refresh()

    let timer: number | null = null
    const scheduleRefresh = (): void => {
      if (timer !== null) {
        window.clearTimeout(timer)
      }
      timer = window.setTimeout(() => {
        timer = null
        refresh()
      }, REFRESH_DEBOUNCE_MS)
    }

    missionManager.on('workspace', scheduleRefresh)
    missionManager.on('change', scheduleRefresh)

    return () => {
      missionManager.off('workspace', scheduleRefresh)
      missionManager.off('change', scheduleRefresh)
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
      const current = workspacesRef.current
      if (current) {
        disposeWorkspaces(current)
        workspacesRef.current = null
      }
      // 遅延退避していた 1 世代前の Map もここで確実に破棄する（リーク防止）。
      const pending = pendingDisposeRef.current
      if (pending && pending !== current) {
        disposeWorkspaces(pending)
      }
      pendingDisposeRef.current = null
    }
  }, [refresh])

  return { scene, workspaces, refreshKey, refresh }
}
